// Saved filter settings ("Presets") for menus with a filter row; stored per account by
// backend/src/routes/filter-presets.ts. Every menu stores the same outer shape so the list can describe a preset;
// `view` holds the menu's own options (Value/%, Daily/Cumulative, tickers, …) and each menu reads it back defensively
// because an old preset may lack newer keys.

export type FilterPresetPage =
  | 'accdist' | 'volume' | 'sid' | 'foreign-flow' | 'ai-monitoring' | 'free-float' | 'hcl' | 'insider';

export interface FilterSettings {
  filters: Record<string, string>;                                  // the filter row: column key → expression
  sort: { key: string; direction: 'asc' | 'desc' } | null;
  view?: Record<string, unknown>;
}

export interface FilterPreset {
  id: string;
  kind: 'named' | 'recent';
  name: string | null;
  settings: FilterSettings;
  created_at: string;
  updated_at: string;
}

// Same settings → same key, whatever the order of the object keys (the database returns its own order)
const sortKeys = (v: unknown): unknown =>
  Array.isArray(v) ? v.map(sortKeys)
    : v && typeof v === 'object' ? Object.fromEntries(Object.keys(v as object).sort().map(k => [k, sortKeys((v as any)[k])])) : v;
export const settingsKey = (s: FilterSettings) => JSON.stringify(sortKeys(s));

export const activeFilters = (s: FilterSettings | undefined) =>
  Object.entries(s?.filters ?? {}).filter(([, v]) => typeof v === 'string' && v.trim() !== '');

/** "3 filters · sort Value ↓" */
export const presetSummary = (s: FilterSettings, label: (key: string) => string = k => k) => {
  const n = activeFilters(s).length;
  const parts = [n ? `${n} filter${n > 1 ? 's' : ''}` : 'no filters'];
  if (s?.sort?.key) parts.push(`sort ${label(s.sort.key)} ${s.sort.direction === 'asc' ? '↑' : '↓'}`);
  return parts.join(' · ');
};

/** One line per filter, for the hover text: "Value: >1B" */
export const presetDetail = (s: FilterSettings, label: (key: string) => string = k => k) =>
  activeFilters(s).map(([k, v]) => `${label(k)}: ${v}`).join('\n') || 'No filters';

/** Only string values, as the filter inputs hold them (an old or edited preset never puts other types in state) */
export const cleanFilters = (f: unknown): Record<string, string> =>
  Object.fromEntries(Object.entries(f && typeof f === 'object' ? f as Record<string, unknown> : {})
    .filter(([, v]) => typeof v === 'string' && v.trim() !== '') as [string, string][]);
